import { useId, useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { buttonClass } from "@/components/buttonStyles";
import { messageFor } from "@/lib/errors";
import {
  DEFAULT_MESSAGE_TOPIC,
  MESSAGE_BODY_MAX,
  MESSAGE_TOPICS,
  messageTopicLabel,
  sendSubmissionMessage,
  type MessageTopic,
} from "@/lib/submissionMessages";

// BRK-S425 — the one composer the Messages tab and the header's Message
// Underwriter dialog share: topic chips (the closed set, General question
// first), the message, and Send. It posts {topic, body} to the order's thread
// (PMS-S842) and reports a send only from the 2xx (NN #7) — the
// host re-reads the thread to show it. A refusal stays inline with the text
// kept, so nothing typed is lost.

export function SubmissionMessageComposer({
  submissionId,
  label,
  note,
  onSent,
  onCancel,
}: {
  submissionId: string;
  /** The message box's label: the tab's and the dialog's differ. */
  label: string;
  /** The line under the box. */
  note: string;
  /** After the 2xx. */
  onSent: () => void;
  /** A dialog's Cancel, beside Send. */
  onCancel?: () => void;
}) {
  const { authedRequest } = useAuth();
  const topicLabelId = useId();
  const noteId = useId();
  const [topic, setTopic] = useState<MessageTopic>(DEFAULT_MESSAGE_TOPIC);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (text === "") {
      setError("Write a message before sending.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sendSubmissionMessage(authedRequest, submissionId, {
        topic,
        body: text,
      });
      setBody("");
      setTopic(DEFAULT_MESSAGE_TOPIC);
      onSent();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const send = (
    <button type="submit" disabled={busy} className={buttonClass()}>
      {busy ? "Sending…" : "Send message"}
    </button>
  );

  return (
    <form
      className="flex min-w-0 flex-col gap-3"
      onSubmit={onSubmit}
      noValidate
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <span id={topicLabelId} className="text-sm font-medium text-brk-text">
          Topic
        </span>
        {/* One of five, always visible: chips rather than a select, with
            `aria-pressed` carrying which is chosen. They wrap at 375px. */}
        <div
          role="group"
          aria-labelledby={topicLabelId}
          className="flex min-w-0 flex-wrap gap-2"
        >
          {MESSAGE_TOPICS.map((t) => {
            const on = t === topic;
            return (
              <button
                key={t}
                type="button"
                aria-pressed={on}
                onClick={() => setTopic(t)}
                // The small secondary button; the chosen topic is
                // tinted, not filled, so Send stays the view's one filled
                // accent. Styled off `aria-pressed` itself: a variant class
                // sorts after the button's own colours, so it wins.
                className={`${buttonClass({ variant: "secondary", size: "sm" })} aria-pressed:border-brk-accent aria-pressed:bg-brk-accent/10 aria-pressed:text-brk-accent`}
              >
                {messageTopicLabel(t)}
              </button>
            );
          })}
        </div>
      </div>
      <label className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-medium text-brk-text">{label}</span>
        <textarea
          value={body}
          rows={4}
          maxLength={MESSAGE_BODY_MAX}
          aria-describedby={noteId}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
        />
      </label>
      <p id={noteId} className="text-sm text-brk-dim">
        {note}
      </p>
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {onCancel ? (
        // Mobile first: stacked full-width at 375px with Send on top; side by
        // side from `sm:`, Send on the right, as a dialog's actions sit.
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className={buttonClass({ variant: "secondary" })}
          >
            Cancel
          </button>
          {send}
        </div>
      ) : (
        <div className="flex">{send}</div>
      )}
    </form>
  );
}
